import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Activity, Bell, LogOut, Search, User } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { useNetworkScanJobOptional } from '../../contexts/NetworkScanJobContext';
import { usePilotContext } from '../../hooks/usePilotContext';
import { api } from '../../lib/api';
import { NavigationHelpDialog } from '../NavigationHelpDialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const PAGE_INDEX = [
  { path: '/dashboard', label: 'Overview', keys: ['overview', 'dashboard', 'home', 'pilot'] },
  {
    path: '/operations',
    label: 'Fusion command',
    keys: ['fusion', 'operations', 'soc', 'ir', 'cti', 'edr', 'xdr', 'triage', 'playbook', 'sandbox', 'bounty', 'peris'],
  },
  {
    path: '/devguard',
    label: 'DevGuard',
    keys: ['devguard', 'developer', 'founder', 'repo', 'website', 'security scan', 'vibe coding'],
  },
  {
    path: '/fusion/cyber-range',
    label: 'Cyber Range',
    keys: ['aertago', 'cyber', 'range', 'dojo', 'red', 'blue', 'lab', 'fusion'],
  },
  { path: '/risk', label: 'Risk', keys: ['risk', 'scan', 'asset', 'cve'] },
  { path: '/assets', label: 'Assets', keys: ['assets', 'cmdb', 'discovery', 'inventory'] },
  { path: '/network-scan', label: 'Network scan', keys: ['network', 'tcp', 'nmap', 'scan'] },
  { path: '/compliance', label: 'Compliance', keys: ['compliance', 'soc2', 'audit', 'attest'] },
  {
    path: '/verification-loop',
    label: 'Verification loop',
    keys: ['verification', 'fix', 'loop', 'pilot', 'readiness'],
  },
  {
    path: '/autonomous-pipeline',
    label: 'Autonomous pipeline',
    keys: ['autonomous', 'red ai', 'pipeline', 'recon', 'cypherfix', 'codefix', 'neo4j', 'aertax', 'kali'],
  },
  { path: '/registry', label: 'Registry', keys: ['registry', 'path', 'attack'] },
  { path: '/forecasts', label: 'Forecasts', keys: ['forecast', 'threat', 'defense'] },
  { path: '/remediation', label: 'Remediation', keys: ['remediation', 'playbook', 'approval'] },
  { path: '/reports', label: 'Reports', keys: ['report', 'pdf', 'html'] },
  { path: '/resimulation', label: 'Resimulation', keys: ['resim', 'replay', 'simulation'] },
  { path: '/events', label: 'Events', keys: ['event', 'websocket', 'live', 'stream'] },
];

function matchPage(query) {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  for (const p of PAGE_INDEX) {
    if (p.label.toLowerCase().includes(q) || p.keys.some((k) => q.includes(k) || k.includes(q))) {
      return p;
    }
  }
  return null;
}

export function TopBar({ wsConnected, lastWsMessage, wsUrl, sidebarCollapsed = false }) {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const netScan = useNetworkScanJobOptional();
  const { context: pilotCtx } = usePilotContext();
  const [search, setSearch] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [alertTotal, setAlertTotal] = useState(0);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [advisoryPayload, setAdvisoryPayload] = useState(null);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [vChrome, setVChrome] = useState('');
  const [vEdge, setVEdge] = useState('');
  const [vResult, setVResult] = useState(null);
  const [vLoading, setVLoading] = useState(false);
  const [authExpired, setAuthExpired] = useState(null); // { at, message, endpoint }

  /** Recent unresolved only — avoids 99+ from years of demo DB rows. */
  const prefetchBadge = useCallback(async () => {
    try {
      const data = await api.get('/api/alerts?limit=1&resolved=false&recent_days=30');
      setAlertTotal(typeof data.total === 'number' ? data.total : 0);
    } catch {
      setAlertTotal(0);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, adv] = await Promise.allSettled([
          api.get('/api/alerts?limit=1&resolved=false&recent_days=30'),
          api.get('/api/security/advisories', { timeoutMs: 12000 }),
        ]);
        if (cancelled) return;
        if (a.status === 'fulfilled') {
          const data = a.value;
          setAlertTotal(typeof data.total === 'number' ? data.total : 0);
        }
        if (adv.status === 'fulfilled') setAdvisoryPayload(adv.value);
        else setAdvisoryPayload(null);
      } catch {
        if (!cancelled) setAlertTotal(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    const onExpired = (ev) => {
      const d = ev?.detail || {};
      setAuthExpired({
        at: Date.now(),
        message: d.message || 'Session expired. Please sign in again.',
        endpoint: d.endpoint || '',
      });
      toast.error('Session expired — sign in again to continue.');
    };
    window.addEventListener('aerta:auth-expired', onExpired);
    return () => window.removeEventListener('aerta:auth-expired', onExpired);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Signed out');
  };

  const loadNotifications = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const [data, adv] = await Promise.all([
        api.get('/api/alerts?limit=8&resolved=false&recent_days=30'),
        api.get('/api/security/advisories', { timeoutMs: 15000 }),
      ]);
      setAlerts(data.alerts || []);
      setAlertTotal(typeof data.total === 'number' ? data.total : (data.alerts || []).length);
      setAdvisoryPayload(adv);
    } catch (e) {
      toast.error(e.message || 'Could not load notifications');
      setAlerts([]);
      prefetchBadge();
    } finally {
      setAlertsLoading(false);
    }
  }, [prefetchBadge]);

  const runVersionCheck = async () => {
    setVLoading(true);
    setVResult(null);
    try {
      const body = {};
      if (vChrome.trim()) body.google_chrome = vChrome.trim();
      if (vEdge.trim()) body.microsoft_edge = vEdge.trim();
      const r = await api.post('/api/security/advisories/check-browser-versions', body, { timeoutMs: 20000 });
      setVResult(r);
      const bad = (r.checks || []).some((c) => c.action_required);
      if (bad) toast.error('One or more builds are below the advisory minimum.');
      else toast.success('Builds meet curated minimums (confirm with vendor release notes).');
    } catch (e) {
      toast.error(e.message || 'Version check failed');
    } finally {
      setVLoading(false);
    }
  };

  const runSearch = () => {
    const hit = matchPage(search);
    if (hit) {
      navigate(hit.path);
      toast.success(`Opened ${hit.label}`);
      setSearch('');
      return;
    }
    toast.error('No match. Try: overview, devguard, risk, registry, reports, events…');
  };

  const wsPreview =
    lastWsMessage == null
      ? 'No messages yet. Open Events to watch the stream.'
      : JSON.stringify(lastWsMessage, null, 2);

  const wsDisplayUrl = (wsUrl || '')
    .replace(/^http:\/\//, 'ws://')
    .replace(/^https:\/\//, 'wss://');

  const dashboardAdvisories = (advisoryPayload?.advisories || []).filter((x) => x.show_on_dashboard);
  const hasAdvisory = dashboardAdvisories.length > 0;
  const combined = Math.min((hasAdvisory ? 1 : 0) + Math.max(0, alertTotal), 99);
  const showBellBadge = combined > 0;
  const badgeLabel = combined > 9 ? '9+' : String(Math.max(0, combined));

  const nsTracked = netScan?.tracked;
  const nsStatus = nsTracked?.job?.status;
  const showNetScanBanner =
    nsTracked &&
    (nsStatus === 'queued' || nsStatus === 'running') &&
    nsTracked.customerId === pilotCtx.customer_id;

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-30 h-16 border-b border-border glass transition-[left] duration-300 ease-out-expo',
        sidebarCollapsed ? 'left-16' : 'left-64'
      )}
    >
      {authExpired ? (
        <div className="absolute inset-x-0 top-0 z-40 border-b border-amber-500/40 bg-amber-500/[0.10] px-6 py-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2 text-[10px]">
            <div className="min-w-0 text-amber-100/90">
              <span className="font-semibold text-amber-100">Session expired.</span>{' '}
              <span className="text-amber-100/80">{authExpired.message}</span>
              {authExpired.endpoint ? (
                <span className="ml-2 font-mono text-[9px] text-amber-100/60">
                  {String(authExpired.endpoint).slice(0, 56)}
                  {String(authExpired.endpoint).length > 56 ? '…' : ''}
                </span>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-7 px-2 text-[10px]"
                onClick={() => window.open('/login', '_blank', 'noopener,noreferrer')}
              >
                Re-login (new tab)
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[10px]"
                onClick={() => {
                  setAuthExpired(null);
                  checkAuth?.();
                }}
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="flex h-full w-full items-center justify-between gap-4 px-6">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="relative w-96 max-w-[min(100%,40vw)]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Jump to page… (type & Enter)"
              className="pl-10 bg-secondary/50 border-secondary"
              data-testid="global-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
            />
          </div>
          {showNetScanBanner && (
            <Link
              to="/network-scan"
              className="hidden max-w-[min(100%,14rem)] shrink-0 items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap rounded-full border border-primary/35 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/15 md:inline-flex"
              title="Network scan in progress — open Network scan"
            >
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              Net scan {nsStatus}…
            </Link>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-4">
          {/* Live — opens connection details */}
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="rounded-md outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Badge variant={wsConnected ? 'success' : 'destructive'} className="cursor-pointer gap-1">
                  <span className={`h-2 w-2 rounded-full ${wsConnected ? 'bg-success' : 'bg-destructive'}`} />
                  {wsConnected ? 'Live' : 'Offline'}
                </Badge>
              </button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Real-time connection</DialogTitle>
                <DialogDescription>
                  WebSocket status and the last payload from the server (system updates, pings, etc.).
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Status:</span>{' '}
                  <strong>{wsConnected ? 'Connected' : 'Disconnected'}</strong>
                </div>
                <div className="break-all">
                  <span className="text-muted-foreground">WebSocket URL:</span> {wsDisplayUrl || '—'}
                </div>
                <Button size="sm" variant="secondary" className="w-full" onClick={() => navigate('/events')}>
                  <Activity className="mr-2 h-4 w-4" />
                  Open Events page
                </Button>
                <pre className="max-h-48 overflow-auto rounded-md border border-border bg-secondary/50 p-3 text-xs font-mono">
                  {wsPreview}
                </pre>
              </div>
            </DialogContent>
          </Dialog>

          <NavigationHelpDialog />

          {/* Notifications: security bulletins + recent unresolved alerts */}
          <DropdownMenu onOpenChange={(open) => open && loadNotifications()}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                data-testid="notifications-btn"
                title="Notifications — security updates & recent alerts"
              >
                <Bell className="h-5 w-5 text-muted-foreground" />
                {showBellBadge ? (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                    {alertsLoading ? '…' : badgeLabel}
                  </span>
                ) : null}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[min(70vh,520px)] w-[380px] overflow-y-auto">
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Last 30 days · unresolved sensor alerts + active security bulletins
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {hasAdvisory && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Security updates
                  </div>
                  {dashboardAdvisories.map((a) => (
                    <div
                      key={a.id}
                      className="border-b border-border/60 px-2 py-2 text-sm last:border-b-0"
                    >
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium leading-snug text-foreground">{a.title}</span>
                        {a.cve_id ? (
                          <span className="rounded bg-secondary px-1.5 font-mono text-[10px] text-muted-foreground">
                            {a.cve_id}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{a.summary}</p>
                      {a.cisa_kev_due_date ? (
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          Tracking: {a.cisa_kev_due_date}
                        </p>
                      ) : null}
                      {a.vendor_minimum_versions && Object.keys(a.vendor_minimum_versions).length > 0 ? (
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          Min builds:{' '}
                          {Object.entries(a.vendor_minimum_versions)
                            .map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`)
                            .join(' · ')}
                        </p>
                      ) : null}
                    </div>
                  ))}
                  <p className="px-2 py-2 text-[11px] leading-relaxed text-muted-foreground">
                    {advisoryPayload?.disclaimer}
                  </p>
                  <DropdownMenuSeparator />
                </>
              )}

              <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Sensor alerts
              </div>
              {alertsLoading && (
                <div className="px-2 py-3 text-sm text-muted-foreground">Loading…</div>
              )}
              {!alertsLoading && alerts.length === 0 && (
                <div className="px-2 py-3 text-sm text-muted-foreground">No recent unresolved alerts.</div>
              )}
              {!alertsLoading &&
                alerts.map((a) => (
                  <DropdownMenuItem
                    key={a.alert_id || a.id}
                    className="flex cursor-default flex-col items-start gap-0.5 py-2"
                    onSelect={(e) => e.preventDefault()}
                  >
                    <span className="font-medium">{a.anomaly_type || a.alert_id}</span>
                    <span className="text-xs text-muted-foreground">
                      {a.severity} · {a.sensor_id}
                    </span>
                  </DropdownMenuItem>
                ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setVersionDialogOpen(true);
                }}
              >
                Compare browser build vs advisory…
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/events')}>Open live events</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Browser build check</DialogTitle>
                <DialogDescription>
                  Paste versions from <span className="font-mono">chrome://version</span> or your MDM export. This
                  compares against curated minimums in the active security bulletin — not a guarantee your org is fully
                  patched.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 text-sm">
                <label className="grid gap-1">
                  <span className="text-muted-foreground">Google Chrome</span>
                  <Input
                    className="font-mono"
                    placeholder="e.g. 146.0.7680.177"
                    value={vChrome}
                    onChange={(e) => setVChrome(e.target.value)}
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-muted-foreground">Microsoft Edge</span>
                  <Input
                    className="font-mono"
                    placeholder="e.g. 146.0.3856.97"
                    value={vEdge}
                    onChange={(e) => setVEdge(e.target.value)}
                  />
                </label>
                <Button
                  type="button"
                  disabled={vLoading || (!vChrome.trim() && !vEdge.trim())}
                  onClick={() => runVersionCheck()}
                >
                  {vLoading ? 'Checking…' : 'Run check'}
                </Button>
                {vResult?.checks?.length ? (
                  <ul className="max-h-40 space-y-2 overflow-y-auto rounded-md border border-border bg-secondary/30 p-2 text-xs">
                    {vResult.checks.map((c) => (
                      <li key={c.advisory_id}>
                        <span className="font-medium">{c.cve_id || c.advisory_id}</span>: {c.summary}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </DialogContent>
          </Dialog>

          <div className="flex items-center gap-2 border-l border-border pl-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-3 rounded-md px-1 py-1 text-left outline-none ring-offset-background hover:bg-secondary/80 focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="text-right">
                    <p className="text-sm font-medium text-foreground">{user?.name || 'User'}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/20">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Account</DropdownMenuLabel>
                <DropdownMenuItem onSelect={() => toast.message('Session', { description: user?.email || 'demo user' })}>
                  Copy session info
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/dashboard')}>Go to Overview</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={handleLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
